/**
 * Utility function to format Firebase timestamps
 * Handles both Firestore Timestamp objects and plain objects with _seconds
 */
export const formatDate = (timestamp: any): string => {
  if (!timestamp) return "N/A";

  try {
    let date: Date;

    // Check if it's a Firestore Timestamp with toDate method
    if (timestamp.toDate && typeof timestamp.toDate === "function") {
      date = timestamp.toDate();
    }
    // Check if it's a plain object with _seconds (from Cloud Functions)
    else if (timestamp._seconds !== undefined) {
      date = new Date(timestamp._seconds * 1000);
    }
    // Check if it's already a Date object
    else if (timestamp instanceof Date) {
      date = timestamp;
    }
    // Try to parse it as a number or string
    else {
      date = new Date(timestamp);
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.error("Invalid date from timestamp:", timestamp);
      return "Invalid Date";
    }

    // Marathi month and weekday names
    const marathiMonths = [
      'जाने.', 'फेब्रु.', 'मार्च', 'एप्रिल', 'मे', 'जून',
      'जुलै', 'ऑगस्ट', 'सप्टें.', 'ऑक्टो.', 'नोव्हें.', 'डिसें.'
    ];
    const marathiWeekdays = [
      'रविवार', 'सोमवार', 'मंगळवार', 'बुधवार', 'गुरुवार', 'शुक्रवार', 'शनिवार'
    ];

    const weekday = marathiWeekdays[date.getDay()];
    const day = date.getDate();
    const month = marathiMonths[date.getMonth()];
    const year = date.getFullYear();

    // Format time as HH:MM AM/PM
    const time = date.toLocaleString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    return `${weekday} | ${day} ${month} ${year}, ${time}`;
  } catch (error) {
    console.error("Error formatting date:", error, timestamp);
    return "Invalid Date";
  }
};

/**
 * Format date in compact format: "Nov 16, 14:25" (24-hour format)
 */
export const formatDateCompact = (timestamp: any): string => {
  if (!timestamp) return "N/A";

  try {
    let date: Date;

    // Check if it's a Firestore Timestamp with toDate method
    if (timestamp.toDate && typeof timestamp.toDate === "function") {
      date = timestamp.toDate();
    }
    // Check if it's a plain object with _seconds (from Cloud Functions)
    else if (timestamp._seconds !== undefined) {
      date = new Date(timestamp._seconds * 1000);
    }
    // Check if it's already a Date object
    else if (timestamp instanceof Date) {
      date = timestamp;
    }
    // Try to parse it as a number or string
    else {
      date = new Date(timestamp);
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return "Invalid Date";
    }

    // Format: "Nov 16, 14:25"
    const month = date.toLocaleDateString("en-US", { month: "short" });
    const day = date.getDate();
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");

    return `${month} ${day}, ${hours}:${minutes}`;
  } catch (error) {
    console.error("Error formatting date:", error, timestamp);
    return "Invalid Date";
  }
};

/**
 * Format date split into date and time for 2-row display
 * Returns { date: "Nov 16", time: "14:25" }
 */
export const formatDateSplit = (timestamp: any): { date: string; time: string } => {
  if (!timestamp) return { date: "N/A", time: "" };

  try {
    let date: Date;

    // Check if it's a Firestore Timestamp with toDate method
    if (timestamp.toDate && typeof timestamp.toDate === "function") {
      date = timestamp.toDate();
    }
    // Check if it's a plain object with _seconds (from Cloud Functions)
    else if (timestamp._seconds !== undefined) {
      date = new Date(timestamp._seconds * 1000);
    }
    // Check if it's already a Date object
    else if (timestamp instanceof Date) {
      date = timestamp;
    }
    // Try to parse it as a number or string
    else {
      date = new Date(timestamp);
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return { date: "Invalid", time: "Date" };
    }

    // Format date: "Nov 16"
    const month = date.toLocaleDateString("en-US", { month: "short" });
    const day = date.getDate();

    // Format time: "14:25"
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");

    return {
      date: `${month} ${day}`,
      time: `${hours}:${minutes}`,
    };
  } catch (error) {
    console.error("Error formatting date:", error, timestamp);
    return { date: "Invalid", time: "Date" };
  }
};
